/**
 * O zoom de profundidade por arrasto nos gráficos de cada vertical (onda 347, R13; spec 10 §4.9) — o pedido do Humberto
 * de 22/10: «no gráfico de cada vertical ao clicar, segurar e arrastar para baixo em cima do gráfico, deve dar zoom para
 * aquele trecho. Ao clicar e arrastar para cima, volta para a escala original».
 *
 *  · `zoomRecortado`: o trecho pedido dentro dos limites do eixo (o fim das leituras), ou null quando sobra menos que o
 *    trecho mínimo;
 *  · `useArrastoVertical`: o gesto — o movimento curto é o clique; o arrasto diz o sentido (para baixo ou para cima, na
 *    tela); no toque, com `toqueLongo`, o arrasto só começa depois do toque longo (sem ele, o dedo rola a página);
 *  · `BarraDoZoom`: «Profundidade de [ ] a [ ] m» e «Ver a vertical inteira», o mesmo pelo teclado e pelo leitor de tela.
 *
 * O zoom vale enquanto a tela está aberta (o estado é de quem desenha) e não se guarda; não muda o PDF.
 */
import type { FormEvent, JSX, PointerEvent, RefObject } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { numeroDigitado } from './escalaDoGrafico';
import { numeroBr } from './escalas';

/** O arrasto mínimo para ser zoom, px (menos é clique) — ESCOLHA DA TELA, SEM FONTE [zoom.arrasto-minimo]. */
export const ARRASTO_DO_ZOOM = 8;
/** O trecho mínimo do zoom, m (menos não vira zoom) — ESCOLHA DA TELA, SEM FONTE [zoom.arrasto-minimo]. */
export const TRECHO_MINIMO_DO_ZOOM = 0.1;
/** O toque longo que começa o zoom no celular, ms — ESCOLHA DA TELA, SEM FONTE [zoom.toque-longo]. */
export const TOQUE_LONGO_DO_ZOOM = 400;

/** Um trecho do eixo, de cima (`de`, o menor) para baixo (`ate`, o maior) — por cota, nos valores da cota. */
export interface TrechoDoZoom {
  de: number;
  ate: number;
}

/**
 * O trecho dentro de `limites` ([menor, maior]), em ordem; null quando não é número ou quando sobra menos que o trecho
 * mínimo (o arrasto além do fim das leituras para no fim delas).
 */
export function zoomRecortado(t: TrechoDoZoom | null | undefined, limites: readonly [number, number]): TrechoDoZoom | null {
  if (!t || !Number.isFinite(t.de) || !Number.isFinite(t.ate)) return null;
  const de = Math.max(limites[0], Math.min(t.de, t.ate));
  const ate = Math.min(limites[1], Math.max(t.de, t.ate));
  if (!(ate - de >= TRECHO_MINIMO_DO_ZOOM - 1e-9)) return null;
  return { de, ate };
}

/** Um arrasto: o valor do eixo onde começou e onde está, e se desce na tela. */
export interface ArrastoVertical {
  inicio: number;
  fim: number;
  desce: boolean;
}

interface EstadoDoArrasto {
  inicio: number;
  y: number;
  arrastou: boolean;
  /**
   * O toque espera o toque longo (`toqueLongo`): só esse prende o dedo (o `touchmove` e o menu). Sem ele, o toque é o de
   * hoje — o navegador rola a página (pan-y) e manda o `pointercancel`.
   */
  comEspera: boolean;
  /** O gesto é do desenho (o mouse, ou o toque depois do toque longo); antes disso, o dedo é da página. */
  armado: boolean;
  relogio: ReturnType<typeof setTimeout> | null;
}

/**
 * O gesto vertical sobre um desenho. Devolve os tratadores do ponteiro (para pôr no elemento que recebe o gesto) e
 * prende, na `caixa`, o `touchmove` do dedo armado (sem isso o navegador rolaria a página no meio do arrasto).
 */
export function useArrastoVertical<E extends Element>(o: {
  caixa: RefObject<HTMLElement | null>;
  /** O valor do eixo no ponteiro. */
  valor(e: PointerEvent<E>): number;
  /** O movimento, px, abaixo do qual é clique. */
  minimo: number;
  /** No toque, o arrasto só depois do toque longo. */
  toqueLongo: boolean;
  /** Prende o ponteiro já ao apertar (as trilhas, como hoje); sem isso, só quando o arrasto começa. */
  prenderAoApertar: boolean;
  aoPassar?(v: number): void;
  aoClicar?(v: number): void;
  aoPrevia(a: ArrastoVertical | null): void;
  aoSoltar(a: ArrastoVertical): void;
}): {
  onPointerDown(e: PointerEvent<E>): void;
  onPointerMove(e: PointerEvent<E>): void;
  onPointerUp(e: PointerEvent<E>): void;
  onPointerCancel(): void;
} {
  const ops = useRef(o);
  ops.current = o;
  const estado = useRef<EstadoDoArrasto | null>(null);

  const largar = (): void => {
    const s = estado.current;
    if (s?.relogio) clearTimeout(s.relogio);
    estado.current = null;
  };

  useEffect(() => {
    const el = o.caixa.current;
    if (!el) return undefined;
    const prender = (ev: Event): void => {
      const s = estado.current;
      if (s?.comEspera && s.armado && ev.cancelable) ev.preventDefault();
    };
    const semMenu = (ev: Event): void => { if (estado.current?.comEspera) ev.preventDefault(); };
    el.addEventListener('touchmove', prender, { passive: false });
    el.addEventListener('contextmenu', semMenu);
    return () => {
      el.removeEventListener('touchmove', prender);
      el.removeEventListener('contextmenu', semMenu);
      const s = estado.current;
      if (s?.relogio) clearTimeout(s.relogio);
    };
  }, [o.caixa]);

  const prenderPonteiro = (alvo: Element, id: number): void => {
    // O evento sintético (ou o ponteiro que já saiu) não tem o que prender: o gesto segue sem a captura.
    try { alvo.setPointerCapture?.(id); } catch { /* nada */ }
  };

  return {
    onPointerDown(e) {
      if (e.button !== 0) return;
      largar();
      // Uma seleção de texto da página que cruzasse o desenho viraria arrasto do navegador: ela sai.
      window.getSelection?.()?.removeAllRanges();
      const comEspera = e.pointerType === 'touch' && ops.current.toqueLongo;
      const s: EstadoDoArrasto = {
        inicio: ops.current.valor(e), y: e.clientY, arrastou: false, comEspera, armado: !comEspera, relogio: null,
      };
      estado.current = s;
      const alvo = e.currentTarget;
      const id = e.pointerId;
      if (!comEspera && ops.current.prenderAoApertar) prenderPonteiro(alvo, id);
      if (comEspera) {
        s.relogio = setTimeout(() => {
          if (estado.current !== s) return;
          s.relogio = null;
          s.armado = true;
          prenderPonteiro(alvo, id);
          ops.current.aoPrevia({ inicio: s.inicio, fim: s.inicio, desce: true });
        }, TOQUE_LONGO_DO_ZOOM);
      }
    },
    onPointerMove(e) {
      const s = estado.current;
      if (!s) {
        if (e.pointerType === 'mouse') ops.current.aoPassar?.(ops.current.valor(e));
        return;
      }
      const dy = e.clientY - s.y;
      if (!s.armado) {
        // O dedo andou antes do toque longo: é da página (rola), não do desenho.
        if (Math.abs(dy) >= ops.current.minimo) largar();
        return;
      }
      if (!s.arrastou && Math.abs(dy) < ops.current.minimo) return;
      if (!s.arrastou && !ops.current.prenderAoApertar) prenderPonteiro(e.currentTarget, e.pointerId);
      s.arrastou = true;
      ops.current.aoPrevia({ inicio: s.inicio, fim: ops.current.valor(e), desce: dy > 0 });
    },
    onPointerUp(e) {
      const s = estado.current;
      largar();
      if (!s) return;
      const v = ops.current.valor(e);
      if (s.arrastou) {
        ops.current.aoPrevia(null);
        ops.current.aoSoltar({ inicio: s.inicio, fim: v, desce: e.clientY - s.y > 0 });
        return;
      }
      if (s.comEspera && s.armado) ops.current.aoPrevia(null);
      ops.current.aoClicar?.(v);
    },
    onPointerCancel() {
      largar();
      ops.current.aoPrevia(null);
    },
  };
}

/** O número do campo: duas casas («5,00»). */
const paraCampo = (v: number): string => (Number.isFinite(v) ? numeroBr(v, 2) : '');

/**
 * A barra do zoom: «Profundidade de [ ] a [ ] m» (ou «Cota de …»), «Ver o trecho» (ou Enter) e «Ver a vertical
 * inteira». Os campos mostram o zoom em uso; o trecho que não vale diz por quê e não se aplica.
 */
export function BarraDoZoom(p: {
  zoom: TrechoDoZoom | null;
  /** Os limites do eixo: [menor, maior] (o começo e o fim das leituras). */
  limites: readonly [number, number];
  rotulo: 'Profundidade' | 'Cota';
  aoZoom(z: TrechoDoZoom | null): void;
}): JSX.Element {
  const id = useId();
  const [de, setDe] = useState(() => (p.zoom ? paraCampo(p.zoom.de) : ''));
  const [ate, setAte] = useState(() => (p.zoom ? paraCampo(p.zoom.ate) : ''));
  const [erro, setErro] = useState<string | null>(null);
  const zDe = p.zoom?.de ?? null;
  const zAte = p.zoom?.ate ?? null;
  useEffect(() => {
    setDe(zDe === null ? '' : paraCampo(zDe));
    setAte(zAte === null ? '' : paraCampo(zAte));
    setErro(null);
  }, [zDe, zAte]);

  const aplicar = (e: FormEvent): void => {
    e.preventDefault();
    const a = numeroDigitado(de);
    const b = numeroDigitado(ate);
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      setErro('Escreva os dois números do trecho.');
      return;
    }
    const z = zoomRecortado({ de: a, ate: b }, p.limites);
    if (!z) {
      setErro(`O trecho tem de ter ao menos ${numeroBr(TRECHO_MINIMO_DO_ZOOM, 2)} m entre ${paraCampo(p.limites[0])} e `
        + `${paraCampo(p.limites[1])} m.`);
      return;
    }
    setErro(null);
    p.aoZoom(z);
  };

  return (
    <form className="cptu-zoom" onSubmit={aplicar} aria-label={`Zoom de ${p.rotulo.toLowerCase()}`} noValidate>
      <label htmlFor={`${id}-de`} className="cptu-zoom-texto">{p.rotulo} de</label>
      <input id={`${id}-de`} name="de" className="cptu-zoom-campo" inputMode="decimal" autoComplete="off" value={de}
        aria-label={`${p.rotulo}: de (m)`} aria-invalid={erro ? true : undefined} aria-describedby={erro ? `${id}-erro` : undefined}
        onChange={(e) => setDe(e.target.value)} />
      <label htmlFor={`${id}-ate`} className="cptu-zoom-texto">a</label>
      <input id={`${id}-ate`} name="ate" className="cptu-zoom-campo" inputMode="decimal" autoComplete="off" value={ate}
        aria-label={`${p.rotulo}: até (m)`} aria-invalid={erro ? true : undefined} aria-describedby={erro ? `${id}-erro` : undefined}
        onChange={(e) => setAte(e.target.value)} />
      <span className="cptu-zoom-texto">m</span>
      <button type="submit" className="cptu-zoom-acao">Ver o trecho</button>
      <button type="button" className="cptu-zoom-acao" disabled={!p.zoom} onClick={() => p.aoZoom(null)}>Ver a vertical inteira</button>
      {erro ? <p id={`${id}-erro`} className="cptu-zoom-erro" role="alert">{erro}</p> : null}
    </form>
  );
}
